import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Lock } from "lucide-react";
import { api, errorMessage } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { RoleEditor } from "./RoleEditor";

const RoleCard = ({ role, total, onEdit, onDelete }) => {
  const { t, pick } = useLang();
  return (
  <div className="flex flex-col rounded-3xl border border-slate-200/80 bg-white p-5" data-testid={`role-card-${role.key}`}>
    <div className="flex items-start gap-3">
      <div className="me-auto">
        <p className="font-display text-lg font-bold text-slate-900">{pick(role.name_en, role.name_ar)}</p>
        {role.name_ar && <p className="text-start text-sm text-slate-500">{pick(role.name_ar, role.name_en)}</p>}
      </div>
      {role.is_system && <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">{t("System")}</span>}
    </div>
    {role.description && <p className="mt-2 text-sm text-slate-500">{role.description}</p>}
    <div className="mt-5 flex items-center gap-4 text-xs font-semibold text-slate-600">
      <span data-testid={`role-perm-count-${role.key}`}>{t("{count}/{total} permissions", { count: role.permissions.length, total })}</span>
      <span data-testid={`role-members-${role.key}`}>{t("{count} members", { count: role.member_count })}</span>
    </div>
    <div className="mt-5 flex gap-2">
      {role.key === "admin" ? (
        <span className="inline-flex items-center gap-1.5 text-xs text-slate-400"><Lock className="h-3.5 w-3.5" /> {t("Locked, always full access")}</span>
      ) : (
        <button onClick={() => onEdit(role)} data-testid={`role-edit-${role.key}`}
          className="btn-press inline-flex min-h-[42px] items-center gap-2 rounded-xl border border-slate-300 px-4 text-sm font-semibold hover:border-slate-900">
          <Pencil className="h-4 w-4" /> {t("Edit")}
        </button>
      )}
      {!role.is_system && (
        <button onClick={() => onDelete(role)} data-testid={`role-delete-${role.key}`}
          className="btn-press inline-flex min-h-[42px] items-center gap-2 rounded-xl px-3 text-sm font-semibold text-slate-500 hover:text-wade">
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </div>
  </div>
  );
};

export const RolesPanel = () => {
  const { t, pick } = useLang();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => { api.get("/admin/roles").then(({ data }) => setData(data)); }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (role) => {
    if (!window.confirm(t("Delete the role \"{name}\"?", { name: pick(role.name_en, role.name_ar) }))) return;
    try { await api.delete(`/admin/roles/${role.key}`); toast.success(t("Role deleted")); load(); }
    catch (e) { toast.error(errorMessage(e)); }
  };

  if (!data) return <div className="h-64 animate-pulse rounded-3xl bg-slate-200" />;
  if (editing) return (
    <RoleEditor role={editing.role} permissions={data.permissions}
      onDone={(saved) => { setEditing(null); if (saved) load(); window.scrollTo({ top: 0 }); }} />
  );
  return (
    <div className="space-y-6" data-testid="roles-panel">
      <div className="flex flex-wrap items-end gap-4">
        <p className="me-auto max-w-xl text-sm text-slate-500">
          {t("Create roles such as HR Manager and choose exactly what they can do. You can only give permissions you have yourself.")}
        </p>
        <button onClick={() => setEditing({ role: null })} data-testid="add-role-button"
          className="btn-press inline-flex min-h-[50px] items-center gap-2 rounded-2xl bg-cobalt px-5 font-semibold text-white hover:bg-cobalt-hover">
          <Plus className="h-5 w-5" /> {t("Add role")}
        </button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {data.roles.map((r) => (
          <RoleCard key={r.key} role={r} total={data.permissions.length} onEdit={(role) => setEditing({ role })} onDelete={remove} />
        ))}
      </div>
    </div>
  );
};
